import React from "react";
import { pipe } from "fp-ts/lib/function";
import { TRoadblockDayViewListItem } from "../../../../../../domain/codecs/form/RoadblockDayViewForm";
import { CRMColors } from "../../../../models/CRMColors";
import { CRMIcon } from "../../../CRMIcon/CRMIcon";
import { CRMSpacing } from "../../../../models/CRMSpacing";
import { CRMZIndex } from "../../../../models/CRMZIndex";
import { SpacingRow } from "../../../BuildingBlocks/SpacingRow";
import { CRMCasesTaskRowBody, CRMCasesTaskRowCell, CRMCasesTaskRowEditableCell, CRMCasesTaskRowIconCell, CRMCasesTaskRowSameCaseCell } from "../CRMCasesTaskRow/CRMCasesTaskRow";
import { capitalizeEveryWord } from "../../../../../../shared/src/utilsByDomain/string/capitalizeEveryWord";
import { underscoreCaseToNormalCase } from "../../../../../../shared/src/utilsByDomain/string";
import { toDateFormat1 } from "../../../../../../shared/src/dateTime";
import { createChangeRouteAction } from "../../../../state/router/createChangeRouteAction";
import { makeJobLinkOutOfRouteAction } from "../../../CRMJobs/CRMJobsOverlay/CRMJobsCardLinkGenerators";
import { Background } from "../../../BuildingBlocks/Background";
import { WeightBold } from "../../../WeightBold/WeightBold";
import { CRMAssignUserAvatar } from "../../../Complex/CRMAssignUserAvatar/CRMAssignUserAvatar";
import { useOpenClose } from "../../../../hooks/UseOpenClose";
import { Relative } from "../../../BuildingBlocks/Relative";
import { Absolute } from "../../../BuildingBlocks/Absolute";
import { TPlainUser } from "../../../../../../domain/codecs/User";
import { TRoadblockAwaitableEntity } from "../../../../../../domain/codecs/form/RoadblockForm";
import { CRMRoadblockAwaitPopout } from "../../../CRMRoadblock/CRMRoadblockAwaitPopout/CRMRoadblockAwaitPopout";
import { getPersistedAwaitRecentEntityIds, pushPersistedAwaitRecentEntityId } from "../../../../state/actions/awaitRecentEntitiesPersistence";
import { CRMPill } from "../../../Simple/CRMPill/CRMPill";
import { WeightSemiBold } from "../../../WeightSemiBold/WeightSemiBold";

type TCRMDayRoadblockRowProps = {
    roadblock: TRoadblockDayViewListItem;
    assignableUsers: Array<TPlainUser>;
    awaitableEntities: Array<TRoadblockAwaitableEntity>;
    isDateShown?: boolean;
    isCaseContinuation?: boolean;
    onChangeAssignee: (payload: { roadblock_id: string, assigned_to_user_id: string }) => void;
    onAwait: (payload: { roadblock_id: string, awaitable_entities_id: string, days_override: number | null }) => void;
    onCancelAwait: (payload: { roadblock_id: string }) => void;
};

const goToRoadblock = (roadblock: TRoadblockDayViewListItem) =>
    makeJobLinkOutOfRouteAction(
        createChangeRouteAction(
            "VIEW_CRM_LEGAL_CASE",
            { caseId: roadblock.case_id },
            { visible_tab: "details" },
            [
                { type: "case_details", meta: {} },
                { type: "case_overview", meta: {} },
                { type: "case_roadblocks", meta: { openRoadblockID: roadblock.id } },
            ],
        ),
    );

const roadblockTypeLabel = (type: TRoadblockDayViewListItem["roadblock_type"]) =>
    pipe(type, underscoreCaseToNormalCase, capitalizeEveryWord);

const effectiveDueDate = (roadblock: TRoadblockDayViewListItem): string => {
    const raw = roadblock.blocked_till ?? roadblock.sla;
    return toDateFormat1(raw);
};

const bodyLine = (roadblock: TRoadblockDayViewListItem): { label: string; value: string; extra?: string } => {
    if (roadblock.status === "awaiting_signoff") {
        return { label: "", value: "Sign off needed" };
    }
    if (roadblock.status === "blocked") {
        return roadblock.blocked_waiting_on_is_internal
            ? { label: "", value: "Parked" }
            : { label: "Chase: ", value: roadblock.blocked_waiting_on ?? "external party" };
    }
    const remaining = Math.max(0, roadblock.unresolved_count - 1);
    return {
        label: "Next: ",
        value: roadblock.next_item?.display_name ?? "No outstanding items",
        extra: remaining > 0 ? ` (+${remaining} more)` : undefined,
    };
};

const statusIconColour = (status: TRoadblockDayViewListItem["status"]): typeof CRMColors.NEUTRAL_8 => {
    switch (status) {
        case "blocked": return CRMColors.HIGHLIGHTS_INSTRUCTIONAL_BRICK_8;
        case "awaiting_signoff": return CRMColors.HIGHLIGHTS_CALMING_PINK_8;
        case "open":
        case "resolved":
        case "suspended":
        default:
            return CRMColors.NEUTRAL_8;
    }
};

export const CRMDayRoadblockRow = (props: TCRMDayRoadblockRowProps): JSX.Element => {
    const { roadblock } = props;
    const body = bodyLine(roadblock);
    const isDateShown = props.isDateShown !== false;
    const openCloseCalendar = useOpenClose();
    const [isAssignPopoutOpen, setIsAssignPopoutOpen] = React.useState(false);
    const IsActive = openCloseCalendar.isOpen || isAssignPopoutOpen;

    const onClickAddress = () => window.open(goToRoadblock(roadblock), "_blank");

    const onPickAwaitableEntity = (awaitable_entities_id: string, days_override: number | null) => {
        pushPersistedAwaitRecentEntityId(awaitable_entities_id);
        props.onAwait({ roadblock_id: roadblock.id, awaitable_entities_id, days_override });
        openCloseCalendar.setIsOpen(false);
    };

    const onCancelAwait = () => {
        props.onCancelAwait({ roadblock_id: roadblock.id });
        openCloseCalendar.setIsOpen(false);
    };

    const activeAwait =
        roadblock.status === "blocked" && roadblock.blocked_till !== null
            ? {
                waiting_on: roadblock.blocked_waiting_on_is_internal
                    ? "Parked"
                    : roadblock.blocked_waiting_on ?? "external party",
                blocked_till: roadblock.blocked_till,
                is_internal: roadblock.blocked_waiting_on_is_internal,
            }
            : null;

    const onChangeAssignee = (assigned_to_user_id: string | null) => {
        if (assigned_to_user_id) {
            props.onChangeAssignee({ roadblock_id: roadblock.id, assigned_to_user_id });
        }
    };

    return (
        <CRMCasesTaskRowBody isClickable isActive={IsActive}>
            <SpacingRow
                spacing={CRMSpacing.SMALL}
                childSize="24px 1fr"
                alignItems="flex-start"
            >
                <CRMCasesTaskRowIconCell
                    iconName="roadblock"
                    colour="hex"
                    hexColour={statusIconColour(roadblock.status)}
                />

                {/* Shared day-view grid: date / address / content / assignee — matching
                    CRMCasesTaskRowChangeable so interleaved rows line up column-for-column. */}
                <SpacingRow
                    spacing={CRMSpacing.MEDIUM}
                    childSize={isDateShown ? "80px 1fr 3fr 40px" : "24px 1fr 3fr 40px"}
                    alignItems="flex-start"
                >
                    {/* CELL - DUE DATE (clickable: await on entity) */}
                    {/* Lifted while open, else the assignee avatars of the rows below —
                        which sit at SECOND_Z — paint over this popout. */}
                    <Relative
                        divRef={openCloseCalendar.ref}
                        zIndex={openCloseCalendar.isOpen ? CRMZIndex.FOREMOST_Z : undefined}
                    >
                        <CRMCasesTaskRowEditableCell
                            weight={700}
                            onClick={openCloseCalendar.toggleOpen}
                        >
                            {isDateShown
                                ? effectiveDueDate(roadblock)
                                : <CRMIcon
                                    iconName="calendar"
                                    colour="hex"
                                    hexColour={CRMColors.NEUTRAL_6}
                                    size="small"
                                    title="Await an external party"
                                />
                            }
                        </CRMCasesTaskRowEditableCell>

                        {openCloseCalendar.isOpen &&
                            <Absolute top="25px" left="5px">
                                <CRMRoadblockAwaitPopout
                                    entities={props.awaitableEntities}
                                    activeAwait={activeAwait}
                                    recentEntityIds={getPersistedAwaitRecentEntityIds()}
                                    bulkCount={null}
                                    listMaxHeightPx={null}
                                    onPick={onPickAwaitableEntity}
                                    onCancel={onCancelAwait}
                                    onRequestClose={() => openCloseCalendar.setIsOpen(false)}
                                />
                            </Absolute>
                        }
                    </Relative>

                    {!props.isCaseContinuation &&
                        <CRMCasesTaskRowCell onClick={onClickAddress}>
                            {roadblock.address}
                        </CRMCasesTaskRowCell>
                    }
                    {props.isCaseContinuation &&
                        <CRMCasesTaskRowSameCaseCell onClick={onClickAddress} />
                    }

                    <CRMCasesTaskRowCell weight={400} onClick={onClickAddress}>
                        <WeightBold>{roadblockTypeLabel(roadblock.roadblock_type)}</WeightBold>
                        {roadblock.case_in_chase &&
                            <>
                                {" "}
                                <CRMPill colour="highlights-calming-pink-10" mode="single">
                                    <WeightSemiBold>
                                        In chase
                                    </WeightSemiBold>
                                </CRMPill>
                            </>
                        }
                        {" — "}
                        {body.label && <WeightBold>{body.label}</WeightBold>}
                        {body.value}
                        {body.extra ?? ""}
                    </CRMCasesTaskRowCell>

                    {/* CELL - ASSIGNED USER (clickable: reassign) */}
                    <Background display="flex" padding={`6px 0px`}>
                        <CRMAssignUserAvatar
                            size="small"
                            value={roadblock.assigned_to_user.id}
                            onChange={onChangeAssignee}
                            onToggleOpen={setIsAssignPopoutOpen}
                            users={props.assignableUsers}
                        />
                    </Background>
                </SpacingRow>
            </SpacingRow>
        </CRMCasesTaskRowBody>
    );
};
